/**
 * reusable register flow component
 *
 * On successful submission, an email will be sent to the user with a link to set their password and complete registration via existing onboarding util methods
 *
 */
// import primary libraries
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation, Link, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';

// actions
import { sendSubmitSelfSignup } from '../../groupUser/groupUserStore.js';
import { sendVerificationEmail } from '../authStore.js';

// utils
import apiUtils from '../../../global/utils/api.js';
import { isSafeUrl, safeRedirect } from '../../../global/utils/linkUtils.js';

// import user components
import { EmailInput, PasswordInput, TextInput, validateEmail } from '../../../global/components/forms/index.js';
import Spinner from '../../../global/components/helpers/Spinner.jsx';
import StyledButton from 'shared-resources/components/src/StyledButton';
import CheckboxInput from '../../../global/components/forms/CheckboxInput.jsx';

const UserRegisterSubmitEmail = ({
  wrapperClass
  , logoUrl = 'https://pro-ficiency-static.s3.us-east-2.amazonaws.com/atlas/slp_full_blue.png' // default logo - params only work when value is undefined, but not when it's null
  , welcomeMessage = 'Enter your email to register a new account to view all content and resources from Simulations Plus.'
  , title = 'Sign up'
  , handleClose
}) => {

  const dispatch = useDispatch();
  const location = useLocation();

  const [userEmail, setUserEmail] = useState('');
  const [inputEmail, setInputEmail] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [otherParams, setOtherParams] = useState({});

  const finalLogoUrl = logoUrl || 'https://pro-ficiency-static.s3.us-east-2.amazonaws.com/atlas/slp_full_blue.png';

  // Extract query params from URL on mount
  React.useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const params = {};
    for (const [key, value] of searchParams.entries()) {
      params[key] = value;
    }
    setOtherParams(params);

    // Pre-populate email field if email or username is in query params
    const rawEmail = params.email || params.username;
    if (rawEmail) {
      // we want to preserve the + here. only replace the first occurance. if there are two +'s, it will remain invalid.
      const emailFromParams = rawEmail.replace(' ', '+');
      setInputEmail(emailFromParams);
      setIsEmailValid(validateEmail(emailFromParams));
    }
  }, [location.search]);

  const handleSubmitEmail = async (e) => {
    setIsLoading(true);
    setError('');
    e.preventDefault();
    // this need to hit an api to send the verification email with an expiring hex code
    // route to a new view where user can enter verification code
    // after the user gets email and enters correct code, then they will be prompted to set their password
    const result = await dispatch(sendVerificationEmail({ email: inputEmail, otherParams }));
    // generic error message for security reasons
    if (!result?.payload?.success) {
      setIsLoading(false);
      setUserEmail(inputEmail);
      setError(result?.payload?.message || result?.error?.message || "There was a problem submitting your email. Please try again");
    } else {
      setIsLoading(false);
      // after api call response
      // if success - setUser and display "Check your email" message, with reset button
      setUserEmail(inputEmail);
    }
  }

  const resetForm = () => {
    setError('');
    setUserEmail('');
    setInputEmail('');
  }

  return (
    <div className={wrapperClass}>
      {!userEmail ? (
        // <div className='flex flex-col gap-5 px-2 bg-neutral-pri max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-4rem)] w-full max-w-full'>
        <div className='flex flex-col gap-5 px-2 bg-neutral-pri w-full max-w-full'>
          <div className='flex justify-center md:p-4'>
            <img className="w-auto h-auto max-h-[30px]" src={finalLogoUrl} alt='logo' />
          </div>
          <div className='flex flex-col gap-2'>
            <h1 className='text-neutral-sec text-3xl m-0'> {title} </h1>
            <p className="text-sm">{welcomeMessage}</p>
          </div>

          {/* Registration Form */}
          <form name='userRegisterForm' onSubmit={handleSubmitEmail} className='min-w-[200px] md:w-full'>
            <EmailInput
              name='username'
              value={inputEmail}
              change={e => setInputEmail(e.target.value)}
              autoFocus={true}
              placeholder="name@company.com"
              onValidationChange={setIsEmailValid}
            />
            <StyledButton
              className='w-full'
              disabled={!inputEmail || !isEmailValid || isLoading}
              type='submit'
            >
              {isLoading ? "Loading..." : "Continue →"}
            </StyledButton>
          </form>
        </div>
      ) : (
        <div className='flex flex-col gap-5 px-2 bg-neutral-pri overflow-y-auto w-full max-w-full'>
          <div className='flex justify-center md:p-4'>
            <img className="w-auto h-auto max-h-[30px]" src={finalLogoUrl} alt='logo' />
          </div>

          {isLoading ? (
            <Spinner size={32} className="mx-auto my-4" />
          ) : (
            <div>
              <div className="mb-4">
                {!error ? (
                  <h1 className="text-neutral-sec m-0">Success!</h1>
                ) : (
                  <h1 className="text-error m-0">Something went wrong</h1>
                )}
              </div>

              {!error ? (
                <div className="flex flex-col gap-4">
                  <p>
                    We've sent an email to{" "}
                    <span className="font-semibold">{userEmail}</span>.
                  </p>
                  <p>
                    Check your inbox and click the verification link to finish signing
                    up. The link is valid for 15 minutes.
                  </p>
                  <p>If you didn't receive an email, click Resend below.</p>

                  <StyledButton
                    variant="primary"
                    size="sm"
                    onClick={handleSubmitEmail}
                    className="w-full"
                    disabled={isLoading}
                  >
                    {isLoading ? "Resending..." : "Resend email"}
                  </StyledButton>
                </div>
              ) : (
                <div className="flex flex-col gap-1 bg-error border-2 border-error p-4 rounded">
                  <p>Oops! Something went wrong.</p>
                  <p>{error || "Please try again."}</p>

                  <StyledButton
                    variant="primary"
                    size="sm"
                    onClick={resetForm}
                    className="w-full mt-4"
                  >
                    Try again
                  </StyledButton>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

UserRegisterSubmitEmail.propTypes = {
  wrapperClass: PropTypes.string
  , logoUrl: PropTypes.string
  , welcomeMessage: PropTypes.string
  , title: PropTypes.string
  , handleClose: PropTypes.func
}

const LoginOptionsHeader = ({ userEmail, resetForm, resetLink, children, logoUrl }) => {

  return (
    <>
      <img className="w-auto h-auto max-h-[40px] mb-8" src={logoUrl} alt='logo' />
      <h1 className='text-neutral-sec text-5xl'> Welcome! </h1>
      {resetLink ? (
        <Link className='text-2xl text-neutral-pri mt-8 font-semibold hover:cursor-pointer hover:no-underline group' to={isSafeUrl(resetLink)} onClick={resetForm}>
          <span>{userEmail}</span>
          <span className='text-transparent text-sm group-hover:text-error transition-all ml-2'>clear email</span>
        </Link>
      ) : (
        <div className='text-2xl text-neutral-pri mt-8 font-semibold hover:cursor-pointer hover:no-underline group' onClick={resetForm}>
          <span>{userEmail}</span>
          <span className='text-transparent text-sm group-hover:text-error transition-all ml-2'>clear email</span>
        </div>
      )
      }
      {children}
    </>
  )
}

LoginOptionsHeader.propTypes = {
  userEmail: PropTypes.string
  , resetForm: PropTypes.func
  , resetLink: PropTypes.string
  , logoUrl: PropTypes.string
  , children: PropTypes.node
}


const getFriendlyError = (error) => {
  switch (error) {
    // ref returns on SessionController samlLogin and samlLoginCallback
    case 'provider_error_1':
      return 'Failed to Initiate SAML Login with Provider';
    case 'provider_error_2':
      return 'Error Authenticating with SAML Login Provider';
    case 'provider_error_3':
      return 'Failed to Authenticate with SAML Login Provider';
    case 'no_matching_user_1':
      return 'Unable to Locate Matching User. Please try again.';
    case 'no_matching_user_2':
      return 'Unable to Locate Matching User. Please try again.';
    case 'not_allowed_1':
      return 'Permission Denied. Please try again.';
    case 'not_allowed_2':
      return 'Unable To Log In As Global Admin Via SSO. Please Contact Admin.';
    default:
      return error;
  }
}

export default UserRegisterSubmitEmail;
